import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner, Badge, Card } from 'react-bootstrap';
import moment from 'moment';
import { obtenerHistorialActividad } from '../../services/actividades/actividadService';
import Swal from 'sweetalert2';
import './ModalHistorialActividad.css';

const ModalHistorialActividad = ({ show, onHide, actividad }) => {
  const [loading, setLoading] = useState(false);
  const [historial, setHistorial] = useState([]);

  useEffect(() => {
    if (show && actividad) {
      cargarHistorial();
    }
  }, [show, actividad]);

  const cargarHistorial = async () => {
    try {
      setLoading(true);
      const response = await obtenerHistorialActividad(actividad._id);
      if (response?.data.data) {
        const historialData = Array.isArray(response.data.data) ? response.data.data : [];
        setHistorial(historialData);
      }
    } catch (error) {
      console.error('Error al cargar historial:', error);
      Swal.fire({ icon: 'error', text: 'Error al cargar el historial de cambios' });
    } finally {
      setLoading(false);
    }
  };

  const formatearCambios = (cambios) => {
    if (!cambios || typeof cambios !== 'object') return null;

    return Object.entries(cambios).map(([campo, datos]) => {
      let valorAnterior = datos.anterior;
      let valorNuevo = datos.nuevo;

      // Formatear fechas
      if (campo.includes('Fecha') && valorAnterior) {
        valorAnterior = moment(valorAnterior).format('DD/MM/YYYY');
      }
      if (campo.includes('Fecha') && valorNuevo) {
        valorNuevo = moment(valorNuevo).format('DD/MM/YYYY');
      }

      // Formatear nombres de campos
      const nombresCampos = {
        FechaInicio: 'Fecha de Inicio',
        FechaVencimiento: 'Fecha de Vencimiento',
        frecuencia: 'Frecuencia',
        proveedorRef: 'Proveedor',
        observaciones: 'Observaciones',
        estado: 'Estado',
        requisicionId: 'Requisición Asociada'
      };

      const nombreCampo = nombresCampos[campo] || campo;

      return (
        <div key={campo} className="mb-2">
          <strong>{nombreCampo}:</strong>
          <div className="d-flex align-items-center gap-2 mt-1">
            <Badge bg="secondary">{valorAnterior || 'N/A'}</Badge>
            <i className="bi bi-arrow-right"></i>
            <Badge bg="primary">{valorNuevo || 'N/A'}</Badge>
          </div>
        </div>
      );
    });
  };

  const getIconoCambio = (tipo) => {
    switch (tipo) {
      case 'creacion':
        return <i className="bi bi-plus-circle-fill text-success"></i>;
      case 'actualizacion':
        return <i className="bi bi-pencil-fill text-warning"></i>;
      case 'cambio_estado':
        return <i className="bi bi-arrow-left-right text-info"></i>;
      case 'requisicion':
        return <i className="bi bi-clipboard-check-fill text-primary"></i>;
      default:
        return <i className="bi bi-circle-fill text-secondary"></i>;
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-clock-history me-2"></i>
          Historial de Cambios
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Cargando historial...</p>
          </div>
        ) : historial.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <i className="bi bi-inbox" style={{ fontSize: '3rem' }}></i>
            <p className="mt-3">No hay cambios registrados para esta actividad</p>
          </div>
        ) : (
          <>
            <Card className="w-100 mb-3 bg-light">
              <Card.Body>
                <h6 className="mb-2">
                  <i className="bi bi-info-circle me-2"></i>
                  Información de la Actividad
                </h6>
                <div className="row">
                  <div className="col-md-6">
                    <p className="mb-1"><strong>Equipo:</strong> {actividad?.equipo?.item}</p>
                    <p className="mb-1"><strong>Tipo:</strong> {actividad?.tipoActividad}</p>
                  </div>
                  <div className="col-md-6">
                    <p className="mb-1"><strong>Estado:</strong> <Badge bg={actividad?.estado === 'Abierta' ? 'success' : 'secondary'}>{actividad?.estado}</Badge></p>
                  </div>
                </div>
              </Card.Body>
            </Card>

            <div className="timeline-historial">
              {historial.map((evento, index) => (
                <div key={index} className="timeline-item-historial">
                  <div className="timeline-marker-historial">
                    {getIconoCambio(evento.tipo)}
                  </div>
                  <Card className="w-100 timeline-card-historial">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <h6 className="mb-1">{evento.descripcion || 'Cambio registrado'}</h6>
                          <small className="text-muted">
                            <i className="bi bi-calendar3 me-1"></i>
                            {moment(evento.fecha).format('DD/MM/YYYY HH:mm')}
                          </small>
                        </div>
                        {evento.usuario && (
                          <Badge bg="dark">
                            <i className="bi bi-person-fill me-1"></i>
                            {evento.usuario.nombre || evento.usuario.email}
                          </Badge>
                        )}
                      </div>

                      {evento.razonCambio && (
                        <div className="alert alert-warning py-2 mb-2">
                          <strong><i className="bi bi-chat-left-quote me-2"></i>Razón:</strong>
                          <br />
                          <em>{evento.razonCambio}</em>
                        </div>
                      )}

                      {evento.cambios && (
                        <div className="mt-2">
                          <small className="text-muted d-block mb-2">Cambios realizados:</small>
                          {formatearCambios(evento.cambios)}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </div>
              ))}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalHistorialActividad;
